import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  showConfirmationPrompt,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@bpinternal/ui-kit-next'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { ArrowRight, BarChart3, Calendar, ChevronDown, MoreVertical, Plus } from 'lucide-react'
import { DateTimeFormatOptions } from 'luxon'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout'
import AutosizeInput from 'react-input-autosize'
import { Boundary, Page } from '~/components'
import { DropdownMenu, EmptyState, Link, MenuItem } from '~/elements'
import {
  AnalyticsBoardConfig,
  Chart,
  chartColorNames,
  ChartConfig,
  ChartDataProvider,
  chartTypes,
  DatePickerAnalyticsPopover,
  dateShortcutDetails,
  defaultDataKeys,
  getRangeFromShortcut,
} from '~/features/analytics'
import { defaultChartLayout, maxChartsPerPage, minW, version } from '~/features/analytics/constants'
import '~/features/analytics/styles/gridLayout.css'
import { useSetAccountPreferences, useSetWorkspacePreferences } from '~/hooks'
import { userPreferencesQueryOptions, workspacePreferencesQueryOptions } from '~/queries'
import { getQueryOptions } from '~/services'
import { cn } from '~/utils'

const ResponsiveGridLayout = WidthProvider(Responsive)
type PreferenceType = 'user' | 'workspace'

export const CustomAnalytics = () => {
  const { botId, workspaceId } = useParams({ from: '/workspaces/$workspaceId/bots/$botId/analytics' })
  const {
    data: { plan },
  } = useSuspenseQuery(getQueryOptions('workspaces_/$workspaceId_', { workspaceId }))
  const { data: userLayoutPreferences } = useSuspenseQuery(
    userPreferencesQueryOptions({ path: '$workspaceId/$botId/analyticsGridBoard', params: { botId, workspaceId } })
  )
  const { data: workspaceLayoutPreferences } = useSuspenseQuery(
    workspacePreferencesQueryOptions(workspaceId, { path: 'analyticsGridBoard/$botId', params: { botId } })
  )
  const { mutate: updateUserBoard } = useSetAccountPreferences()
  const { mutate: updateWorkspaceBoard } = useSetWorkspacePreferences(workspaceId)

  const [preferenceType, setPreferenceType] = useState<PreferenceType>('user')

  const [currentAnalyticsBoard, setCurrentAnalyticsBoard] = useState<AnalyticsBoardConfig>(
    preferenceType === 'user' ? userLayoutPreferences : workspaceLayoutPreferences
  )
  const { layouts, charts } = currentAnalyticsBoard

  // TODO: These checks are scattered across the app. We should have a single place to check for plan.
  const customAnalyticsEnabled = plan !== 'community' // MINIMUM_PLAN and above
  const tooltipUnsupportedPlanMessage = 'Only available on Plus plans and above.'

  const [transition, setTransition] = useState(false)

  useEffect(() => {
    if (!transition) {
      setTransition(true)
    }
  }, [transition])

  const updateLayoutPreferences = useCallback(() => {
    if (!customAnalyticsEnabled) return
    if (preferenceType === 'user') {
      updateUserBoard({
        path: '$workspaceId/$botId/analyticsGridBoard',
        params: { botId, workspaceId },
        value: { ...currentAnalyticsBoard, charts: currentAnalyticsBoard.charts.slice(0, maxChartsPerPage) },
      })
    } else {
      updateWorkspaceBoard({
        path: 'analyticsGridBoard/$botId',
        params: { botId },
        value: { ...currentAnalyticsBoard, charts: currentAnalyticsBoard.charts.slice(0, maxChartsPerPage) },
      })
    }
  }, [
    customAnalyticsEnabled,
    preferenceType,
    updateUserBoard,
    botId,
    workspaceId,
    currentAnalyticsBoard,
    updateWorkspaceBoard,
  ])

  const handlePreferenceChange = (value: PreferenceType) => {
    setPreferenceType(value)
    updateLayoutPreferences()
    setCurrentAnalyticsBoard(value === 'user' ? userLayoutPreferences : workspaceLayoutPreferences)
  }

  const addChartMenu: MenuItem[] = chartTypes.map((type) => {
    const parsedType = type.replace('_', ' ')
    return {
      content: parsedType.charAt(0).toUpperCase() + parsedType.slice(1),
      type: 'item',
      onSelect: () => {
        const newId = nanoid()
        setCurrentAnalyticsBoard((prev) => ({
          ...prev,
          charts: [
            ...prev.charts,
            {
              id: newId,
              type,
              title: 'New Chart',
              values: [],
              showLegend: true,
              dateRange: { shortcut: 'lastMonth' },
            },
          ],
          layouts: {
            lg: [...prev.layouts.lg, { x: 0, y: 99, w: 5, h: 2, minW, i: newId }],
            sm: [...prev.layouts.sm, { x: 0, y: 99, w: 5, h: 2, minW, i: newId }],
          },
        }))
        updateLayoutPreferences()
      },
    }
  })

  const editBoardMenu: MenuItem[] = [
    {
      type: 'submenu',
      content: 'Add Chart',
      items: addChartMenu,
      hidden: charts.length >= maxChartsPerPage,
    },
    {
      type: 'item',
      content: 'Add Chart',
      tooltip: 'You have reached the maximum number of charts allowed.',
      disabled: true,
      hidden: charts.length < maxChartsPerPage,
    },

    {
      type: 'item',
      content: 'Back to Default',
      onSelect: () =>
        void showConfirmationPrompt(
          'Are you sure you want to reset the analytics board to its default layout? This action cannot be undone.',
          {
            title: 'Reset Analytics Board',
          }
        ).then(() => {
          setCurrentAnalyticsBoard(defaultChartLayout)
          updateLayoutPreferences()
        }),
    },
    { type: 'separator' },
    {
      type: 'item',
      content: 'Remove All Charts',
      danger: true,
      onSelect: () =>
        void showConfirmationPrompt('Are you sure you want to delete all charts? This action cannot be undone.', {
          variant: 'danger',
          title: 'Delete All Charts',
        }).then(() => {
          setCurrentAnalyticsBoard((prev) => ({ ...prev, charts: [] }))
          updateLayoutPreferences()
        }),
    },
  ]

  const children = useMemo(
    () =>
      charts.map(({ id, title, type, values, showLegend, dateRange }) => {
        const updateChart = (key: keyof ChartConfig, value: any) => {
          setCurrentAnalyticsBoard((prev) => ({
            ...prev,
            charts: prev.charts.map((chart) => (chart.id === id ? { ...chart, [key]: value } : chart)),
          }))
          updateLayoutPreferences()
        }
        const chartMenuItems: MenuItem[] = [
          {
            type: 'checkBoxItem',
            content: showLegend ? 'Hide Legend' : 'Show Legend',
            checked: showLegend,
            showCheckmark: false,
            onCheckedChange: (checked) => updateChart('showLegend', checked),
          },
          {
            type: 'submenu',
            content: 'Type',
            items: [
              {
                type: 'radioGroup',
                value: type,
                items: chartTypes.map((chartType) => {
                  const parsedType = chartType.replace('_', ' ')
                  return {
                    content: parsedType.charAt(0).toUpperCase() + parsedType.slice(1),
                    value: chartType,
                    type: 'radioItem',
                    onSelect: () => updateChart('type', chartType),
                  }
                }),
              },
            ],
          },
          {
            content: 'Duplicate',
            type: 'item',
            disabled: charts.length >= maxChartsPerPage,
            tooltip:
              charts.length >= maxChartsPerPage ? 'You have reached the maximum number of charts allowed.' : undefined,
            onSelect: () => {
              const chart = charts.find((chartObj) => chartObj.id === id)
              const lgLayout = layouts.lg.find((layout) => layout.i === id)
              const smLayout = layouts.sm.find((layout) => layout.i === id)
              const newId = nanoid()
              if (chart) {
                setCurrentAnalyticsBoard((prev) => ({
                  version,
                  charts: [...prev.charts, { ...chart, id: newId }],
                  layouts: {
                    lg: [...prev.layouts.lg, { x: 0, y: 0, w: 4, h: 2, ...lgLayout, minW, i: newId }],
                    sm: [...prev.layouts.sm, { x: 0, y: 0, w: 4, h: 2, ...smLayout, minW, i: newId }],
                  },
                }))
                updateLayoutPreferences()
              }
            },
          },
          { type: 'separator' },
          {
            content: 'Delete',
            danger: true,
            type: 'item',
            onSelect: () => {
              setCurrentAnalyticsBoard((prev) => ({
                ...prev,
                charts: prev.charts.filter((chart) => chart.id !== id),
              }))
              updateLayoutPreferences()
            },
          },
        ]

        const getFormattedRangeDate = () => {
          if (dateRange?.start && dateRange.end) {
            const formatting: DateTimeFormatOptions = {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            }
            const formattedFrom = new Date(dateRange.start).toLocaleDateString('en-US', formatting)
            const formattedTo = new Date(dateRange.end).toLocaleDateString('en-US', formatting)
            return (
              <div className="flex gap-1">
                {formattedFrom}
                <ArrowRight />
                {formattedTo}
              </div>
            )
          } else if (dateRange?.shortcut) {
            return <>{dateShortcutDetails[dateRange.shortcut ?? 'lastMonth'].label}</>
          }
        }

        const datePickerButton = (
          <Button className="no-drag group shadow-none" size="sm" variant="outline" disabled={!customAnalyticsEnabled}>
            <span className="hidden @md:block">{getFormattedRangeDate()}</span>
            <Calendar className="h-4 w-4 group-disabled:opacity-50 @md:hidden" />
          </Button>
        )

        const dataSourceButton = (
          <Button
            data-no-dnd="true"
            disabled={!customAnalyticsEnabled}
            size="sm"
            className="no-drag group shadow-none"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <span className="hidden @sm:block">Edit Source</span>
            <BarChart3 className="h-4 w-4 group-disabled:opacity-50 @sm:hidden" />
          </Button>
        )

        const chartOptionsButton = (
          <Button size="sm" variant="ghost" disabled={!customAnalyticsEnabled} className="px-2">
            <MoreVertical />
          </Button>
        )

        return (
          <div key={id} className="group">
            <Boundary className="size-full">
              <ChartDataProvider
                botId={botId}
                workspaceId={workspaceId}
                dateShortcut={dateRange?.shortcut}
                startDate={dateRange?.start}
                endDate={dateRange?.end}
              >
                {({ data, resolution }) => (
                  <Chart
                    resolution={resolution}
                    className="overflow-visible @container"
                    showLegend={showLegend}
                    type={type as any}
                    categories={values as any}
                    data={data}
                    key={id}
                  >
                    <div className="flex items-center gap-2">
                      {customAnalyticsEnabled ? (
                        <>
                          <div
                            className={cn('no-drag group ml-2 mr-auto truncate text-sm font-medium', {
                              'border-b border-transparent focus-within:!border-gray-9 hover:border-gray-5': title,
                            })}
                          >
                            <AutosizeInput
                              placeholder="Chart Title"
                              inputClassName="[all:unset] hover:placeholder-gray-8 focus:placeholder-gray-8 placeholder-transparent"
                              value={title}
                              onChange={(e) => updateChart('title', e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && (e.target as any).blur()}
                            />
                          </div>
                          <DatePickerAnalyticsPopover
                            dateRange={
                              dateRange?.shortcut
                                ? getRangeFromShortcut(dateRange.shortcut)
                                : {
                                    from: dateRange?.start ? new Date(dateRange.start) : undefined,
                                    to: dateRange?.end ? new Date(dateRange.end) : undefined,
                                  }
                            }
                            dateShortcut={dateRange?.shortcut}
                            onShortcutSelect={(shortcut) => {
                              updateChart('dateRange', { shortcut, start: undefined, end: undefined })
                            }}
                            onRangeSelect={(range) => {
                              if (range?.from && range?.to)
                                updateChart('dateRange', { shortcut: undefined, start: range?.from, end: range?.to })
                            }}
                          >
                            {datePickerButton}
                          </DatePickerAnalyticsPopover>
                          <Popover>
                            <PopoverTrigger asChild>{dataSourceButton}</PopoverTrigger>
                            <PopoverContent className="no-drag" collisionPadding={50}>
                              <div
                                className="flex flex-col overflow-y-auto"
                                style={{ maxHeight: 'var(--radix-popover-content-available-height)' }}
                              >
                                {[...new Set([...defaultDataKeys, ...data.flatMap((d) => Object.keys(d))])]
                                  .filter((key) => key !== 'timestamp')
                                  .map((key) => (
                                    <label
                                      key={key}
                                      className={cn(
                                        'flex cursor-pointer items-center rounded-md p-2 hover:bg-gray-2 hover:text-gray-12',
                                        {
                                          'text-gray-10': !chartColorNames[values.indexOf(key)],
                                        }
                                      )}
                                      style={{
                                        color: chartColorNames[values.indexOf(key)]
                                          ? `var(--${chartColorNames[values.indexOf(key)]}-11)`
                                          : undefined,
                                      }}
                                    >
                                      <Checkbox
                                        checked={values.includes(key)}
                                        onCheckedChange={(checked) => {
                                          const newValues = checked
                                            ? [...values, key].slice(-10)
                                            : values.filter((v) => v !== key)
                                          updateChart('values', newValues)
                                          updateLayoutPreferences()
                                        }}
                                        className="mr-2 border-gray-7 shadow-none"
                                      />
                                      <span className="truncate text-sm">{key}</span>
                                    </label>
                                  ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                          <DropdownMenu content={chartMenuItems}>{chartOptionsButton}</DropdownMenu>
                        </>
                      ) : (
                        <>
                          <span className="ml-2 mr-auto truncate text-sm font-medium">{title}</span>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>{datePickerButton}</TooltipTrigger>
                              <TooltipContent>{tooltipUnsupportedPlanMessage}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>{dataSourceButton}</TooltipTrigger>
                              <TooltipContent>{tooltipUnsupportedPlanMessage}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>{chartOptionsButton}</TooltipTrigger>
                              <TooltipContent>{tooltipUnsupportedPlanMessage}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </>
                      )}
                    </div>
                  </Chart>
                )}
              </ChartDataProvider>
            </Boundary>
            {customAnalyticsEnabled && (
              <div className="absolute bottom-1 right-1 hidden size-1.5 border-b-2 border-r-2 border-gray-8 group-hover:block" />
            )}
          </div>
        )
      }),
    [charts, customAnalyticsEnabled, botId, workspaceId, updateLayoutPreferences, layouts.lg, layouts.sm]
  )

  const editBoardButton = (
    <Button disabled={!customAnalyticsEnabled} variant="outline">
      Edit Board
      <ChevronDown className="ml-2 h-4 w-4" />
    </Button>
  )

  const LayoutPreferenceTabs = () => (
    <Tabs defaultValue={preferenceType} onValueChange={(value) => handlePreferenceChange(value as PreferenceType)}>
      <TabsList>
        <TabsTrigger value="user">User</TabsTrigger>
        <TabsTrigger value="workspace">Workspace</TabsTrigger>
      </TabsList>
    </Tabs>
  )

  return (
    <Page
      title="Analytics"
      actions={
        customAnalyticsEnabled ? (
          <DropdownMenu modal={false} content={editBoardMenu}>
            {editBoardButton}
          </DropdownMenu>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>{editBoardButton}</TooltipTrigger>
              <TooltipContent>{tooltipUnsupportedPlanMessage}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      }
    >
      <div className="flex flex-col gap-6 @container">
        <div className="flex justify-center">
          <LayoutPreferenceTabs />
        </div>
        {currentAnalyticsBoard.charts.length > 0 ? (
          <div className="flex flex-col gap-6">
            <ResponsiveGridLayout
              isResizable={customAnalyticsEnabled}
              isDraggable={customAnalyticsEnabled}
              verticalCompact
              useCSSTransforms={false}
              className={cn('layout -m-3', { animated: transition })}
              layouts={layouts}
              onLayoutChange={(_, allLayouts) => {
                setCurrentAnalyticsBoard((prev) => ({ ...prev, layouts: allLayouts }) as any)
                updateLayoutPreferences()
              }}
              cols={{ lg: 12, sm: 2 }}
              breakpoints={{ lg: 1024, sm: 0 }}
              rowHeight={100}
              resizeHandles={['se']}
              draggableCancel=".no-drag"
              margin={[12, 12]}
            >
              {children}
            </ResponsiveGridLayout>
            {customAnalyticsEnabled ? (
              charts.length < maxChartsPerPage && (
                <Button
                  variant="outline"
                  className="h-16 border-dashed"
                  size="lg"
                  onClick={() => {
                    const newId = nanoid()
                    setCurrentAnalyticsBoard((prev) => ({
                      ...prev,
                      charts: [
                        ...prev.charts,
                        {
                          id: newId,
                          type: 'area',
                          title: 'New Chart',
                          values: [],
                          showLegend: true,
                          dateRange: { shortcut: 'lastMonth' },
                        },
                      ],
                      layouts: {
                        lg: [...prev.layouts.lg, { x: 0, y: 99, w: 5, h: 2, minW, i: newId }],
                        sm: [...prev.layouts.sm, { x: 0, y: 99, w: 5, h: 2, minW, i: newId }],
                      },
                    }))
                    updateLayoutPreferences()
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Chart
                </Button>
              )
            ) : (
              <div className="flex items-center justify-center gap-2 border border-dashed border-gray-5 p-6">
                <span className="text-sm text-gray-600">
                  Custom Analytics are not available on the Pay-as-you-go plan. Please,
                </span>
                <Link to={'/workspaces/$workspaceId/billing'} params={{ workspaceId }}>
                  upgrade your plan
                </Link>
                <span className="text-sm text-gray-600">to access this feature.</span>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8">
            <EmptyState
              className="mx-auto max-w-[var(--container-2)]"
              icon={BarChart3}
              title={'Create Your Custom Dashboard'}
              description={
                'Start building your personalized analytics dashboard. Add and arrange charts that matter most to you and begin exploring your data insights in a way that suits your needs.'
              }
              primaryAction={
                <DropdownMenu content={addChartMenu} side="right">
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Charts
                  </Button>
                </DropdownMenu>
              }
            />
          </div>
        )}
      </div>
    </Page>
  )
}
